/**
 * 🚗 VEHICLE METADATA SERVICE
 * Service for fetching vehicle types, body types, and other metadata
 */

import { getApiBaseUrl } from '@/config/environment';
import { buildApiUrl } from '@/utils/apiHelpers';

export interface VehicleType {
  id: string;
  typeName: string;
  typeCode: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface VehicleBodyType {
  id: string;
  bodyName: string;
  bodyCode: string;
  description?: string;
  typicalSeating?: number;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

class VehicleMetadataService {
  private async request(endpoint: string, options: RequestInit = {}): Promise<any> {
    const token = localStorage.getItem('token');
    const response = await fetch(`${getApiBaseUrl()}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get vehicle types (economy, standard, comfort, luxury, etc.)
   */
  async getVehicleTypes(includeInactive = false): Promise<{
    success: boolean;
    data?: { vehicleTypes: VehicleType[]; total: number };
    error?: string;
  }> {
    try {
      const queryParams = includeInactive ? '?include_inactive=true' : '';
      const url = buildApiUrl(`/admin/vehicle-types${queryParams}`, 'v1');
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.json();
    } catch (error) {
      console.error('Error fetching vehicle types:', error);
      return {
        success: false,
        error: 'Failed to fetch vehicle types',
      };
    }
  }

  /**
   * Get vehicle body types (sedan, suv, truck, etc.)
   */
  async getVehicleBodyTypes(includeInactive = false): Promise<{
    success: boolean;
    data?: { bodyTypes: VehicleBodyType[]; total: number };
    error?: string;
  }> {
    try {
      const queryParams = includeInactive ? '?include_inactive=true' : '';
      const url = buildApiUrl(`/admin/vehicle-body-types${queryParams}`, 'v1');
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.json();
    } catch (error) {
      console.error('Error fetching vehicle body types:', error);
      return {
        success: false,
        error: 'Failed to fetch vehicle body types',
      };
    }
  }
}

export const vehicleMetadataService = new VehicleMetadataService();
export default vehicleMetadataService;
